import {useEffect, useRef} from 'react';

interface KakaoMapProps {
    initialAddress: string;
    shops?: Array<{
        id: number;
        shopName: string;
        businessAddress: string;
        latitude?: number;
        longitude?: number;
    }>;
    onVisibleShopsChange?: (visibleShopIds: number[]) => void;
}

export default function KakaoMap({initialAddress, shops = [], onVisibleShopsChange}: KakaoMapProps) {
    const mapContainer = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<any>(null);
    const markersRef = useRef<Map<number, any>>(new Map());

    // 카카오맵 스크립트 로드
    useEffect(() => {
        if (!initialAddress) {
            return;
        }

        if ((window as any).kakao?.maps) {
            initMap();
            return;
        }

        const script = document.createElement('script');
        script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${process.env.REACT_APP_KAKAO_API_KEY}&autoload=false&libraries=services`;
        script.async = true;

        script.onload = () => {
            (window as any).kakao.maps.load(() => {
                initMap();
            });
        };

        script.onerror = () => {
            console.error('카카오맵 스크립트 로드 실패');
        };

        document.head.appendChild(script);

        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, [initialAddress, shops]);

    const initMap = () => {
        if (!mapContainer.current || !initialAddress) return;
        if (mapInstance.current) return;

        const kakao = (window as any).kakao;
        const geocoder = new kakao.maps.services.Geocoder();

        geocoder.addressSearch(initialAddress, (result: any, status: any) => {
            if (status === kakao.maps.services.Status.OK) {
                const coords = {
                    lat: parseFloat(result[0].y),
                    lng: parseFloat(result[0].x)
                };
                createMap(coords.lat, coords.lng);
            } else {
                console.error('주소 검색 실패, 기본 위치 사용');
                createMap(37.5665, 126.9780);
            }
        });
    };

    const updateVisibleShops = () => {
        if (!mapInstance.current || !onVisibleShopsChange) return;

        try {
            const kakao = (window as any).kakao;
            const bounds = mapInstance.current.getBounds();
            const visibleIds: number[] = [];

            markersRef.current.forEach((marker, shopId) => {
                const position = marker.getPosition();
                if (bounds.contain(position)) {
                    visibleIds.push(shopId);
                }
            });

            onVisibleShopsChange(visibleIds);
        } catch (error) {
            console.error('필터링 에러:', error);
        }
    };

    const createMap = (lat: number, lng: number) => {
        try {
            const kakao = (window as any).kakao;

            const options = {
                center: new kakao.maps.LatLng(lat, lng),
                level: 5,
                draggable: true,
                scrollwheel: true,
                disableDoubleClick: false,
                disableDoubleClickZoom: false
            };

            mapInstance.current = new kakao.maps.Map(mapContainer.current, options);

            // 줌 컨트롤 추가
            const zoomControl = new kakao.maps.ZoomControl();
            mapInstance.current.addControl(zoomControl, kakao.maps.ControlPosition.RIGHT);

            // 선호 지역 중심에 빨간 마커
            const centerMarkerImage = new kakao.maps.MarkerImage(
                'https://t1.kakaocdn.net/localimg/localimages/07/mapapidoc/marker_red.png',
                new kakao.maps.Size(30, 32)
            );

            new kakao.maps.Marker({
                map: mapInstance.current,
                position: new kakao.maps.LatLng(lat, lng),
                image: centerMarkerImage
            });

            // 지도 이동/줌 이벤트 리스너
            if (onVisibleShopsChange) {
                kakao.maps.event.addListener(mapInstance.current, 'idle', () => {
                    updateVisibleShops();
                });
            }

            // 지도 크기 재조정
            setTimeout(() => {
                if (mapInstance.current) {
                    mapInstance.current.relayout();
                    if (onVisibleShopsChange) {
                        updateVisibleShops();
                    }
                }
            }, 100);

            // 가맹점 마커 추가
            if (shops.length > 0) {
                const geocoder = new kakao.maps.services.Geocoder();

                shops.forEach(shop => {
                    if (shop.latitude && shop.longitude) {
                        addMarker(shop.id, shop.latitude, shop.longitude, shop.shopName);
                    } else if (shop.businessAddress) {
                        geocoder.addressSearch(shop.businessAddress, (result: any, status: any) => {
                            if (status === kakao.maps.services.Status.OK) {
                                addMarker(shop.id, parseFloat(result[0].y), parseFloat(result[0].x), shop.shopName);
                            }
                        });
                    }
                });
            }

        } catch (error) {
            console.error('❌ 지도 생성 실패:', error);
        }
    };

    const addMarker = (shopId: number, lat: number, lng: number, name: string) => {
        try {
            const kakao = (window as any).kakao;
            const position = new kakao.maps.LatLng(lat, lng);

            const marker = new kakao.maps.Marker({
                position: position,
                map: mapInstance.current
            });

            markersRef.current.set(shopId, marker);

            const infowindow = new kakao.maps.InfoWindow({
                content: `<div style="padding:5px;font-size:12px;white-space:nowrap;">${name}</div>`
            });

            kakao.maps.event.addListener(marker, 'click', () => {
                infowindow.open(mapInstance.current, marker);
            });

            if (onVisibleShopsChange) {
                setTimeout(() => updateVisibleShops(), 200);
            }
        } catch (error) {
            console.error('마커 추가 실패:', error);
        }
    };

    // initialAddress 변경 시 지도 이동
    useEffect(() => {
        if (mapInstance.current && initialAddress) {
            const kakao = (window as any).kakao;
            const geocoder = new kakao.maps.services.Geocoder();

            geocoder.addressSearch(initialAddress, (result: any, status: any) => {
                if (status === kakao.maps.services.Status.OK) {
                    const coords = new kakao.maps.LatLng(parseFloat(result[0].y), parseFloat(result[0].x));
                    mapInstance.current.setCenter(coords);

                    if (onVisibleShopsChange) {
                        setTimeout(() => updateVisibleShops(), 200);
                    }
                }
            });
        }
    }, [initialAddress]);

    return (
        <div
            ref={mapContainer}
            style={{
                width: '100%',
                height: '100%',
                touchAction: 'none',
            }}
        />
    );
}